import {
  dayIn,
  includesMember,
  isTaskDue,
  weekdayOf,
  inSeries,
  shiftOn,
  seriesTime,
  type Region,
  MIN_STREAK,
  streakAlive,
  type CalendarEvent,
  type Daypart,
  type Member,
  type Task,
  binName,
  isSchoolFree,
} from '@shh/shared';
import type { FamilyData, WithId } from './family';
import { tasksText } from '../i18n/tasks';
import { calendarText } from '../i18n/calendar';
import { locale } from '../lib/locale';

/**
 * Read-only helpers on top of the live family data. Pages ask questions ("what is due for
 * Lotta this afternoon?"), these functions answer them – the same everywhere.
 */
export type M = WithId<Member>;
export type T = WithId<Task>;
export type E = WithId<CalendarEvent> & {
  /** Generated, not stored: school spans, school/public holidays, birthdays (1.24). */
  virtual?: 'timetable' | 'holiday' | 'birthday' | 'shift' | 'todo';
  /** Shift plans (1.25): the plan and the key of this day. */
  shiftPlanId?: string;
  shiftKey?: string;
  /** A public holiday ("Allerheiligen") rather than school holidays. */
  publicHoliday?: boolean;
  details?: string[];
};

export const memberById = (f: FamilyData, id?: string | null) => f.members.find((m) => m.id === id);
export const kids = (f: FamilyData) => f.members.filter((m) => m.role !== 'parent');
export const parents = (f: FamilyData) => f.members.filter((m) => m.role === 'parent');
export const unitFor = (m?: Pick<Member, 'role' | 'ageProfile'>) =>
  m && m.role === 'teen' ? tasksText.points : tasksText.stars;

// ---------------------------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------------------------

export type DisplayState = 'open' | 'pending' | 'done';

/** Due today – a flexible task only until the day it was done ("1× pro Woche" done Monday). */
const dueToday = (f: FamilyData) =>
  f.tasks.filter((t) => {
    if (!isTaskDue(t, f.today)) return false;
    if (t.repeat.kind !== 'flex') return true;
    const i = f.instances[t.id];
    if (!i || i.status === 'open') return true;
    // stays visible (ticked) on the day it was done, so a mistake can still be undone
    return i.doneAt === undefined || dayIn(f.timeZone, new Date(i.doneAt)) === f.today;
  });

export function stateOf(f: FamilyData, taskId: string): DisplayState {
  const s = f.instances[taskId]?.status;
  return s === 'pending' ? 'pending' : s === 'done' || s === 'approved' ? 'done' : 'open';
}

/** Who took the task today (first come), if anybody. */
export function holderOf(f: FamilyData, taskId: string): string | undefined {
  const i = f.instances[taskId];
  return i && i.status !== 'open' ? i.memberId : undefined;
}

/** Days in a row for a person's own task – only while the streak is still alive, from 2 on. */
export function streakOf(f: FamilyData, memberId: string | undefined, task: T): number | undefined {
  if (!memberId) return undefined;
  const s = f.streaks[memberId + '_' + task.id];
  if (!s || s.count < MIN_STREAK || !streakAlive(task.repeat, s.lastDone, f.today))
    return undefined;
  return s.count;
}

export const isPersonal = (t: T) => Array.isArray(t.assignees) && t.assignees.length === 1;
export const isSharedBetween = (t: T) => Array.isArray(t.assignees) && t.assignees.length > 1;
const isForAll = (t: T) => t.assignees === 'all';

/** A person's own and shared tasks for today (not the "für alle" ones). */
/**
 * A person's tasks today. With a daypart, "anytime" tasks count as due now as well – unless
 * `exact` (lists grouped by daypart must show each task once).
 */
export function tasksOf(f: FamilyData, memberId: string, daypart?: Daypart, exact = false): T[] {
  return dueToday(f)
    .filter((t) => t.assignees !== 'all' && includesMember(t.assignees, memberId))
    .filter((t) => !daypart || t.daypart === daypart || (!exact && t.daypart === 'anytime'))
    .sort(byDaypart);
}

export const forAllTasks = (f: FamilyData) => dueToday(f).filter(isForAll).sort(byDaypart);

const DAYPART_ORDER: Record<Daypart, number> = { morning: 0, afternoon: 1, evening: 2, anytime: 3 };
const byDaypart = (a: T, b: T) => DAYPART_ORDER[a.daypart] - DAYPART_ORDER[b.daypart];

/** Tasks waiting for a parent's OK. */
export const pendingInstances = (f: FamilyData) =>
  Object.values(f.instances).filter((i) => i.status === 'pending');

export function currentDaypart(time: string): Exclude<Daypart, 'anytime'> {
  const h = Number(time.slice(0, 2));
  if (h < 11) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

// ---------------------------------------------------------------------------------------------
// To-dos (no stars)
// ---------------------------------------------------------------------------------------------

/** Done to-dos stay visible on the day they were done, then disappear. */
export function visibleTodo(f: FamilyData, t: FamilyData['todos'][number]) {
  return !t.done || (t.doneAt !== undefined && dayIn(f.timeZone, new Date(t.doneAt)) === f.today);
}

export const todosOf = (f: FamilyData, memberId: string) =>
  f.todos.filter(
    (t) => t.assignees !== 'all' && t.assignees.includes(memberId) && visibleTodo(f, t),
  );
export const familyTodos = (f: FamilyData) =>
  f.todos.filter((t) => t.assignees === 'all' && visibleTodo(f, t));

// ---------------------------------------------------------------------------------------------
// Calendar – one shared calendar, school times included
// ---------------------------------------------------------------------------------------------

export const isFamilyEvent = (e: Pick<CalendarEvent, 'assignees'>) => e.assignees === 'all';
export const involves = (e: Pick<CalendarEvent, 'assignees'>, memberId: string) =>
  includesMember(e.assignees, memberId);
export const participants = (f: FamilyData, e: Pick<CalendarEvent, 'assignees'>) =>
  e.assignees === 'all' ? f.members : f.members.filter((m) => e.assignees.includes(m.id));

// German and English (1.19) – the family names its school holidays in its own language
const NO_SCHOOL =
  /ferien|schulfrei|feiertag|brückentag|kita geschlossen|holiday|half[- ]term|inset day|bank holiday|nursery closed/i;

/** Default timetable names are stored in German ("Schule", "Kita") – shown in the device language. */
export const timetableName = (label?: string) =>
  !label || label === 'Schule'
    ? calendarText.school
    : label === 'Kita'
      ? calendarText.kita
      : label === 'OGS'
        ? calendarText.ogs
        : label;

/**
 * Single, multi-day or a series – every rhythm of Kalender 2 (1.25): daily, weekly, monthly,
 * yearly, with an end, dates taken out and a pause in the school holidays of the family's state.
 */
export function occursOn(
  e: Pick<CalendarEvent, 'date' | 'endDate' | 'repeat'>,
  date: string,
  region?: Region,
) {
  if (!e.repeat) return e.date <= date && (e.endDate ?? e.date) >= date;
  return inSeries(e.date, e.repeat, date, {
    schoolFree: region ? (d) => isSchoolFree(region, d) : undefined,
  });
}

/** Everything on a day: stored events (incl. multi-day and series) and school/kita from the timetables. */
export function eventsOn(f: FamilyData, date: string): E[] {
  const region = f.household?.location?.region;
  const stored: E[] = f.events.flatMap((e) => {
    if (!occursOn(e, date, region)) return [];
    // a series shows on each of its dates – "nur diesen Termin" may have moved the time
    const shown = e.repeat
      ? { ...e, date, ...seriesTime(e.repeat, date, e) }
      : // from a time on one day to a time days later (business trip): the first day shows the
        // start, the days after count as all day – the bar in week and month shows the span
        e.start && e.endDate && e.endDate > e.date
        ? date === e.date
          ? { ...e, end: undefined }
          : { ...e, start: undefined, end: undefined }
        : e;
    // bins keep the waste company's German name in the data; the family reads its language
    return [shown.source === 'waste' ? { ...shown, title: binName(shown.title, locale) } : shown];
  });
  const weekday = weekdayOf(date);
  const school: E[] = [];
  if (weekday <= 5) {
    for (const [memberId, tt] of Object.entries(f.timetables)) {
      const day = tt.days[String(weekday) as '1'];
      if (!day || !memberById(f, memberId)) continue;
      // 1.24: the state's school holidays and public holidays pause timetables by themselves;
      // a "Ferien"/"schulfrei" event still works (other countries, extra days off)
      const off =
        (region !== undefined && isSchoolFree(region, date)) ||
        stored.some((e) => NO_SCHOOL.test(e.title) && involves(e, memberId));
      if (off) continue;
      school.push({
        id: `tt_${memberId}_${date}`,
        title: timetableName(tt.label),
        icon: 'school',
        date,
        start: day.start,
        end: day.end,
        assignees: [memberId],
        bring: [],
        source: 'manual',
        virtual: 'timetable',
        details: day.lessons.map((l, i) => `${i + 1}. ${l}`),
      });
    }
  }
  // shift plans (1.25): the shift of the day for its person; "U" a day off as holiday
  const shifts: E[] = f.shiftPlans.flatMap((plan): E[] => {
    const key = shiftOn(plan, date);
    if (!key || key === '-' || !memberById(f, plan.memberId)) return [];
    const type = key === 'U' ? undefined : plan.types[key as 'F'];
    if (key !== 'U' && !type) return [];
    return [
      {
        id: `shift_${plan.id}_${date}`,
        title: type ? type.name : calendarText.vacation,
        icon: type ? 'calendar' : 'plane',
        date,
        start: type?.start,
        end: type?.end,
        assignees: [plan.memberId],
        bring: [],
        source: 'manual',
        virtual: 'shift',
        shiftPlanId: plan.id,
        shiftKey: key,
        details: type ? [calendarText.shift] : undefined,
      },
    ];
  });
  return [...stored, ...school, ...shifts].sort(
    (a, b) =>
      (a.start ?? '00:00').localeCompare(b.start ?? '00:00') || a.title.localeCompare(b.title),
  );
}

// ---------------------------------------------------------------------------------------------
// Stars
// ---------------------------------------------------------------------------------------------

export const reservedFor = (f: FamilyData, memberId: string) =>
  f.redemptions.filter((r) => r.memberId === memberId).reduce((s, r) => s + r.cost, 0);

export const availableFor = (f: FamilyData, memberId: string) =>
  (memberById(f, memberId)?.balance ?? 0) - reservedFor(f, memberId);

export const goalRewardOf = (f: FamilyData, m: M) =>
  f.rewards.find((r) => r.id === m.goalRewardId) ??
  [...f.rewards]
    .filter((r) => !r.memberIds.length || r.memberIds.includes(m.id))
    .sort((a, b) => a.cost - b.cost)
    .find((r) => r.cost > m.balance) ??
  f.rewards[0];

export const rewardsFor = (f: FamilyData, memberId: string) =>
  f.rewards
    .filter((r) => !r.memberIds.length || r.memberIds.includes(memberId))
    .sort((a, b) => a.cost - b.cost);
